/**
 * Toene per Web Audio, ohne eine einzige Audiodatei.
 *
 * Gruende: keine Ladezeit, kein Ausliefern von Assets, kein Abspielverbot
 * durch die Autoplay-Regeln der Browser (der erste Ton entsteht immer aus
 * einer Berührung heraus), und auf dem Fernseher nichts, was haengen bleibt.
 */

let ctx: AudioContext | null = null
let an = true

type FensterMitAudio = typeof globalThis & { webkitAudioContext?: typeof AudioContext }

function kontext(): AudioContext | null {
  if (ctx) return ctx
  try {
    const W = globalThis as FensterMitAudio
    const Konstruktor = W.AudioContext ?? W.webkitAudioContext
    if (!Konstruktor) return null
    ctx = new Konstruktor()
    return ctx
  } catch {
    return null
  }
}

export function tonSchalten(wert: boolean): void {
  an = wert
}

export function tonIstAn(): boolean {
  return an
}

/** Ein einzelner Ton. Frequenz in Hertz, Dauer in Sekunden. */
function spiele(
  frequenz: number,
  dauer: number,
  verzoegerung = 0,
  form: OscillatorType = 'sine',
  lautstaerke = 0.18,
): void {
  if (!an) return
  const c = kontext()
  if (!c) return
  if (c.state === 'suspended') void c.resume()

  const start = c.currentTime + verzoegerung
  const osz = c.createOscillator()
  const huelle = c.createGain()

  osz.type = form
  osz.frequency.value = frequenz

  // Weiche Huellkurve. Harte Einsaetze klingen nach Fehlerpiepsen, und genau
  // diesen Beiklang soll eine Lern-App fuer Kinder nirgends haben.
  huelle.gain.setValueAtTime(0.0001, start)
  huelle.gain.exponentialRampToValueAtTime(lautstaerke, start + 0.012)
  huelle.gain.exponentialRampToValueAtTime(0.0001, start + dauer)

  osz.connect(huelle)
  huelle.connect(c.destination)
  osz.start(start)
  osz.stop(start + dauer + 0.02)
}

/** Treffer. Heller, kurzer Ton, der nicht im Weg steht. */
export function tonRichtig(kette: number): void {
  // Mit jedem Kettenglied eine Stufe hoeher. Die Tonleiter selbst wird zur
  // Rueckmeldung: Man hoert, dass etwas laeuft, ohne hinzusehen.
  const stufen = [523.25, 587.33, 659.25, 698.46, 783.99, 880.0, 987.77, 1046.5]
  const index = Math.min(stufen.length - 1, Math.max(0, kette - 1))
  spiele(stufen[index] ?? 523.25, 0.16, 0, 'sine', 0.16)
}

/** Kette gezuendet: aufsteigender Dreiklang. */
export function tonKette(): void {
  spiele(523.25, 0.14, 0, 'triangle', 0.15)
  spiele(659.25, 0.14, 0.08, 'triangle', 0.15)
  spiele(783.99, 0.26, 0.16, 'triangle', 0.16)
}

/**
 * Fehler. Bewusst kein Summer, sondern ein weicher, tiefer Zweiklang.
 * Es soll klingen wie "hm, schau nochmal", nicht wie "falsch".
 */
export function tonFalsch(): void {
  spiele(311.13, 0.2, 0, 'sine', 0.13)
  spiele(261.63, 0.3, 0.1, 'sine', 0.12)
}

/** Ballon zerplatzt. Kurzes Knacken aus einem schnellen Abfall. */
export function tonPlop(): void {
  if (!an) return
  const c = kontext()
  if (!c) return
  if (c.state === 'suspended') void c.resume()

  const osz = c.createOscillator()
  const huelle = c.createGain()
  const start = c.currentTime

  osz.type = 'sine'
  osz.frequency.setValueAtTime(880, start)
  osz.frequency.exponentialRampToValueAtTime(180, start + 0.09)

  huelle.gain.setValueAtTime(0.22, start)
  huelle.gain.exponentialRampToValueAtTime(0.0001, start + 0.11)

  osz.connect(huelle)
  huelle.connect(c.destination)
  osz.start(start)
  osz.stop(start + 0.13)
}

/** Rundenende: kleine Fanfare. */
export function tonFertig(): void {
  const melodie: Array<[number, number]> = [
    [523.25, 0],
    [659.25, 0.12],
    [783.99, 0.24],
    [1046.5, 0.36],
  ]
  for (const [f, t] of melodie) spiele(f, 0.3, t, 'triangle', 0.16)
}

/** Weckt den Audiokontext aus einer Berührung heraus. */
export function tonAufwecken(): void {
  const c = kontext()
  if (c && c.state === 'suspended') void c.resume()
}

/**
 * Blitzschnelle Antwort: ein kurzer, heller Dreiklang nach oben.
 *
 * Belohnt genau das, was trainiert wird, naemlich Abrufen statt Rechnen. Im
 * Unterschied zu einer Zufallsbelohnung sagt dieser Ton etwas Wahres, und das
 * Kind kann ihn durch eigenes Koennen wieder herbeifuehren.
 */
export function tonBlitz(): void {
  spiele(1046.5, 0.1, 0, 'triangle', 0.14)
  spiele(1318.5, 0.1, 0.06, 'triangle', 0.14)
  spiele(1568.0, 0.2, 0.12, 'triangle', 0.15)
}

/** Eine neue Farbwelt ist dazugekommen. */
export function tonFreigeschaltet(): void {
  const melodie: Array<[number, number]> = [
    [523.25, 0],
    [659.25, 0.1],
    [783.99, 0.2],
    [1046.5, 0.3],
    [1318.5, 0.42],
  ]
  for (const [f, t] of melodie) spiele(f, 0.35, t, 'triangle', 0.15)
}
